import {
  AppstoreOutlined,
  CheckCircleOutlined,
  CodeOutlined,
  CreditCardOutlined,
  DeleteOutlined,
  FileTextOutlined,
  HomeOutlined,
  LoginOutlined,
  MessageOutlined,
  SearchOutlined,
  ShoppingCartOutlined,
  TagOutlined,
  ThunderboltOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  getLowerCaseLanguageCode,
  getTranslation,
} from '@mahawi/eshop-common/src/translation';
import {
  EEventType,
  type IEvent,
  type IEventContext,
  type ILanguageType,
  type ILeadSession,
  type ISessionCategory,
  type ISessionProduct,
  type ITranslation,
} from '@mahawi/eshop-common/src/types';
import {
  ConfigProvider,
  Popover,
  Space,
  Tag,
  Timeline,
  type TimelineItemProps,
  Tooltip,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import React from 'react';

import { EDateFormat } from '../../fragments/date-format';
import { EMPTY_VALUE } from '../../fragments/empty-value';
import { COLOR, FONT_SIZE, SPACING } from '../../theme/tokens';
import ExternalLink from '../external-link';
import Link from '../link';

enum ETimelineItemKind {
  PRODUCT = 'product',
  CATEGORY = 'category',
  EVENT = 'event',
}

interface ITimelineItem {
  kind: ETimelineItemKind;
  names?: ITranslation[];
  uuid?: string;
  createdAt: Date;
  code?: string;
  context?: IEventContext | null;
}

interface ITimelineGroup {
  identity: string;
  item: ITimelineItem;
  newestAt: Date;
  oldestAt: Date;
  count: number;
}

interface IEventAppearance {
  Icon: typeof SearchOutlined;
  color: string;
}

const TIMELINE_DOT_SIZE = 28;
const TIMELINE_TITLE_SPAN = '6rem';
const TIMELINE_THEME = {
  components: { Timeline: { dotSize: TIMELINE_DOT_SIZE } },
};

const DUPLICATE_EVENT_WINDOW_SECONDS = 60;

const HIDDEN_EVENT_CODES: string[] = [
  EEventType.PAGE_PRODUCT,
  EEventType.PAGE_PRODUCTS,
];

const BASKET_EVENT_CODES: string[] = [
  EEventType.PAGE_BASKET,
  EEventType.PAGE_BASKET_CHANGE_PRODUCT_COUNT,
  EEventType.PRODUCT_ADD_TO_BASKET,
];

const ORDER_EVENT_CODES: string[] = [
  EEventType.PAGE_ORDER,
  EEventType.PAGE_DELIVERY,
  EEventType.ORDER_DELIVERY_ADDRESS_IS_DIFFERENT,
  EEventType.ORDER_DISCOUNT_CODE_VALIDATION,
  EEventType.ORDER_HEUREKA_CZ_SHOP_CERTIFICATION,
  EEventType.ORDER_MARKETING_CONSENT_CHANGE,
  EEventType.ORDER_TERMS_AND_CONDITION,
  EEventType.ORDER_VALIDATION,
];

const ACCOUNT_EVENT_CODES: string[] = [
  EEventType.PAGE_ACCOUNT_ADDRESSES,
  EEventType.PAGE_ACCOUNT_COUPONS,
  EEventType.PAGE_ACCOUNT_ORDER_DETAIL,
  EEventType.PAGE_ACCOUNT_ORDERS,
  EEventType.PAGE_ACCOUNT_OVERVIEW,
  EEventType.PAGE_ACCOUNT_SETTINGS,
  EEventType.PAGE_ACCOUNT_SIGN_IN,
];

function getEventAppearance(code: string): IEventAppearance {
  if (code === EEventType.SEARCH_PRODUCT) {
    return { Icon: SearchOutlined, color: COLOR.purple.primary! };
  }

  if (code === EEventType.ORDER_SUBMITTED) {
    return { Icon: CheckCircleOutlined, color: COLOR.success };
  }

  if (code === EEventType.ORDER_PAYMENT_REDIRECTION) {
    return { Icon: CreditCardOutlined, color: COLOR.orange.primary! };
  }

  if (code === EEventType.PRODUCT_REMOVED_FROM_BASKET) {
    return { Icon: DeleteOutlined, color: COLOR.error };
  }

  if (BASKET_EVENT_CODES.includes(code)) {
    return { Icon: ShoppingCartOutlined, color: COLOR.gold.primary! };
  }

  if (ORDER_EVENT_CODES.includes(code)) {
    return { Icon: FileTextOutlined, color: COLOR.orange.primary! };
  }

  if (ACCOUNT_EVENT_CODES.includes(code)) {
    return { Icon: UserOutlined, color: COLOR.geekblue.primary! };
  }

  if (code === EEventType.MESSENGER_WINDOW_OPEN) {
    return { Icon: MessageOutlined, color: COLOR.primary };
  }

  if (code === EEventType.PAGE_HOME) {
    return { Icon: HomeOutlined, color: COLOR.primary };
  }

  return { Icon: ThunderboltOutlined, color: COLOR.purple.primary! };
}

function humanizeEventCode(code: string): string {
  const words: string = code.toLowerCase().replaceAll('_', ' ');

  return words.charAt(0).toUpperCase() + words.slice(1);
}

function formatGap(seconds: number): string {
  if (seconds < 60) {
    return `+${seconds}s`;
  }

  if (seconds < 3600) {
    return `+${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  }

  return `+${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
}

function getIdentity(item: ITimelineItem): string {
  if (item.kind === ETimelineItemKind.EVENT) {
    return `${item.kind}:${item.code}:${JSON.stringify(item.context ?? {})}`;
  }

  return `${item.kind}:${item.uuid}`;
}

function collectTimelineItems(session: ILeadSession): ITimelineItem[] {
  const items: ITimelineItem[] = [];

  session.products.forEach((product: ISessionProduct): void => {
    items.push({ kind: ETimelineItemKind.PRODUCT, ...product });
  });

  session.categories.forEach((category: ISessionCategory): void => {
    items.push({ kind: ETimelineItemKind.CATEGORY, ...category });
  });

  const lastSeenByCode = new Map<string, Date>();
  session.events
    .slice()
    .sort(
      (a: IEvent, b: IEvent): number =>
        dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
    )
    .forEach((event: IEvent): void => {
      if (HIDDEN_EVENT_CODES.includes(event.code)) {
        return;
      }

      const lastSeen: Date | undefined = lastSeenByCode.get(event.code);
      if (
        lastSeen &&
        Math.abs(dayjs(event.createdAt).diff(dayjs(lastSeen), 'second')) <=
          DUPLICATE_EVENT_WINDOW_SECONDS
      ) {
        return;
      }

      lastSeenByCode.set(event.code, event.createdAt);
      items.push({ kind: ETimelineItemKind.EVENT, ...event });
    });

  return items.sort(
    (a: ITimelineItem, b: ITimelineItem): number =>
      dayjs(b.createdAt).unix() - dayjs(a.createdAt).unix(),
  );
}

function groupConsecutive(items: ITimelineItem[]): ITimelineGroup[] {
  const groups: ITimelineGroup[] = [];

  items.forEach((item: ITimelineItem): void => {
    const identity: string = getIdentity(item);
    const previous: ITimelineGroup | undefined = groups[groups.length - 1];

    if (previous && previous.identity === identity) {
      previous.count += 1;
      previous.oldestAt = item.createdAt;
      return;
    }

    groups.push({
      identity,
      item,
      newestAt: item.createdAt,
      oldestAt: item.createdAt,
      count: 1,
    });
  });

  return groups;
}

function EventContextSummary({
  context,
}: {
  context: IEventContext;
}): React.ReactElement {
  const details: string[] = [];

  if (context.count !== undefined) {
    details.push(`count: ${context.count}`);
  }
  if (context.page) {
    details.push(`page: ${context.page.number}`);
  }
  if (context.discount) {
    details.push(`discount: ${context.discount.code}`);
  }
  if (context.currencyType) {
    details.push(`currency: ${context.currencyType.isoCode}`);
  }
  if (context.languageType) {
    details.push(`language: ${context.languageType.code}`);
  }
  if (context.isEnabled !== undefined) {
    details.push(`enabled: ${context.isEnabled ? 'yes' : 'no'}`);
  }
  if (context.isValid !== undefined) {
    details.push(`valid: ${context.isValid ? 'yes' : 'no'}`);
  }

  const productUuid: string | undefined =
    context.product?.uuid ?? context.relatedProduct?.targetUuid;

  return (
    <Space size={SPACING.sm} wrap>
      {context.action && <Tag>{context.action}</Tag>}

      {context.search && (
        <Typography.Text code>{context.search.query}</Typography.Text>
      )}

      {context.order?.uuid && (
        <Link
          to={`/order/${context.order.uuid}`}
          label={`order ${context.order.uuid.slice(0, 8)}`}
          openInNewTab
        />
      )}

      {productUuid && (
        <ExternalLink
          to={`product/${productUuid}`}
          label={`product ${productUuid.slice(0, 8)}`}
          ariaLabel="Open product in new tab"
        />
      )}

      {context.category?.uuid && (
        <ExternalLink
          to={`category/${context.category.uuid}`}
          label={`category ${context.category.uuid.slice(0, 8)}`}
          ariaLabel="Open category in new tab"
        />
      )}

      {details.length > 0 && (
        <Typography.Text type="secondary">
          {details.join(' · ')}
        </Typography.Text>
      )}

      <Popover
        trigger="click"
        content={
          <pre style={{ margin: SPACING.none, maxWidth: '40rem' }}>
            {JSON.stringify(context, null, 2)}
          </pre>
        }
      >
        <Tooltip title="Raw context">
          <CodeOutlined style={{ cursor: 'pointer', color: COLOR.link }} />
        </Tooltip>
      </Popover>
    </Space>
  );
}

function TimelineItemContent({
  item,
  count,
  languageType,
  configUrl,
}: {
  item: ITimelineItem;
  count: number;
  languageType: ILanguageType | undefined;
  configUrl: string;
}): React.ReactElement {
  const countTag: React.ReactElement | null =
    count > 1 ? <Tag>×{count}</Tag> : null;

  if (item.kind === ETimelineItemKind.EVENT && item.code) {
    const hasContext: boolean =
      !!item.context && Object.keys(item.context).length > 0;

    return (
      <Space orientation="vertical" size={SPACING.xs}>
        <Space size={SPACING.sm} wrap>
          <Typography.Text strong>
            {humanizeEventCode(item.code)}
          </Typography.Text>
          {countTag}
        </Space>
        {hasContext && item.context && (
          <EventContextSummary context={item.context} />
        )}
      </Space>
    );
  }

  const translation: string | null = item.names
    ? getTranslation(item.names, languageType)
    : null;
  const webHref = `${configUrl}/${getLowerCaseLanguageCode(languageType)}/${item.kind}/${item.uuid}`;

  return (
    <Space size={SPACING.sm} wrap>
      <Tag color={item.kind === ETimelineItemKind.PRODUCT ? 'green' : 'blue'}>
        {item.kind}
      </Tag>
      <ExternalLink
        to={`${item.kind}/${item.uuid}`}
        label={translation || EMPTY_VALUE}
        ariaLabel="Open in admin"
      />
      <Tooltip title="Open on web">
        <span>
          <ExternalLink to={webHref} ariaLabel="Open on web" />
        </span>
      </Tooltip>
      {countTag}
    </Space>
  );
}

function TimelineItemTitle({
  group,
  olderAt,
}: {
  group: ITimelineGroup;
  olderAt: Date;
}): React.ReactElement {
  const gapSeconds: number = Math.max(
    0,
    dayjs(group.oldestAt).diff(dayjs(olderAt), 'second'),
  );
  const range: string =
    group.count > 1
      ? `${dayjs(group.oldestAt).format(EDateFormat.DATE_TIME)} – ${dayjs(group.newestAt).format(EDateFormat.DATE_TIME)}`
      : dayjs(group.newestAt).format(EDateFormat.DATE_TIME);

  return (
    <Tooltip title={range}>
      <Space orientation="vertical" size={SPACING.none}>
        <Typography.Text>
          {dayjs(group.newestAt).format(EDateFormat.TIME)}
        </Typography.Text>
        <Typography.Text type="secondary" style={{ fontSize: FONT_SIZE.sm }}>
          {formatGap(gapSeconds)}
        </Typography.Text>
      </Space>
    </Tooltip>
  );
}

const getIconStyle = (color: string): React.CSSProperties => ({
  color,
  fontSize: FONT_SIZE.xl,
});

function getItemIcon(item: ITimelineItem): React.ReactElement {
  if (item.kind === ETimelineItemKind.PRODUCT) {
    return <TagOutlined style={getIconStyle(COLOR.success)} />;
  }

  if (item.kind === ETimelineItemKind.CATEGORY) {
    return <AppstoreOutlined style={getIconStyle(COLOR.primary)} />;
  }

  const appearance: IEventAppearance = getEventAppearance(
    item.code ?? EEventType.PAGE_HOME,
  );

  return <appearance.Icon style={getIconStyle(appearance.color)} />;
}

export default function SessionTimeline({
  session,
  languageType,
  configUrl,
}: {
  session: ILeadSession;
  languageType: ILanguageType | undefined;
  configUrl: string;
}): React.ReactElement {
  const groups: ITimelineGroup[] = groupConsecutive(
    collectTimelineItems(session),
  );

  const items: TimelineItemProps[] = groups.map(
    (group: ITimelineGroup, index: number): TimelineItemProps => {
      const olderGroup: ITimelineGroup | undefined = groups[index + 1];

      return {
        key: `${group.identity}:${index}`,
        icon: getItemIcon(group.item),
        title: (
          <TimelineItemTitle
            group={group}
            olderAt={olderGroup ? olderGroup.newestAt : session.createdAt}
          />
        ),
        content: (
          <TimelineItemContent
            item={group.item}
            count={group.count}
            languageType={languageType}
            configUrl={configUrl}
          />
        ),
      };
    },
  );

  items.push({
    key: 'session-created',
    icon: <LoginOutlined style={getIconStyle(COLOR.red[3]!)} />,
    title: (
      <Tooltip title={dayjs(session.createdAt).format(EDateFormat.DATE_TIME)}>
        <Typography.Text>
          {dayjs(session.createdAt).format(EDateFormat.TIME)}
        </Typography.Text>
      </Tooltip>
    ),
    content: <Typography.Text type="secondary">Session start</Typography.Text>,
  });

  return (
    <ConfigProvider theme={TIMELINE_THEME}>
      <Timeline mode="start" titleSpan={TIMELINE_TITLE_SPAN} items={items} />
    </ConfigProvider>
  );
}
